import campaignApi from '../api/campaigns.api';
import CampaignCard from '../components/campaigns/CampaignCard';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import { useToast } from '../contexts/ToastContext';
import { useCampaigns } from '../hooks/useCampaigns';
import { CampaignStatus } from '../types/campaign.types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Draft', value: CampaignStatus.DRAFT },
    { label: 'Processing', value: CampaignStatus.PROCESSING },
    { label: 'In Progress', value: CampaignStatus.IN_PROGRESS },
    { label: 'Completed', value: CampaignStatus.COMPLETED },
    { label: 'Failed', value: CampaignStatus.FAILED },
    { label: 'Cancelled', value: CampaignStatus.CANCELLED },
];

const CampaignsPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const {
        campaigns,
        loading,
        error,
        pagination,
        fetchCampaigns,
        deleteCampaign,
        stopCampaign,
    } = useCampaigns();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stopModalOpen, setStopModalOpen] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
    const [selectedStopCampaignId, setSelectedStopCampaignId] = useState<number | null>(null);
    const [executeLoadingId, setExecuteLoadingId] = useState<number | null>(null);
    const [stopLoadingId, setStopLoadingId] = useState<number | null>(null);

    const summary = useMemo(() => {
        const total = campaigns.length;
        const completed = campaigns.filter((c) => c.status === CampaignStatus.COMPLETED).length;
        const active = campaigns.filter(
            (c) =>
                c.status === CampaignStatus.PROCESSING ||
                c.status === CampaignStatus.IN_PROGRESS
        ).length;
        const failed = campaigns.filter((c) => c.status === CampaignStatus.FAILED).length;

        return { total, completed, active, failed };
    }, [campaigns]);

    const fetchWithFilters = (overrides?: {
        page?: number;
        search?: string;
        status?: CampaignStatus | '';
    }) => {
        const nextSearch = overrides?.search ?? searchTerm;
        const nextStatus = overrides?.status ?? statusFilter;

        fetchCampaigns({
            page: overrides?.page,
            search: nextSearch.trim() || undefined,
            status: nextStatus || undefined,
        });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchWithFilters({ page: 1 });
    };

    const handleStatusFilter = (status: CampaignStatus | '') => {
        setStatusFilter(status);
        fetchCampaigns({
            page: 1,
            search: searchTerm.trim() || undefined,
            status: status || undefined,
        });
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        fetchCampaigns({ page: 1 });
    };

    const handleDeleteClick = (id: number) => {
        setSelectedCampaignId(id);
        setDeleteModalOpen(true);
    };

    const handleStopClick = (id: number) => {
        setSelectedStopCampaignId(id);
        setStopModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedCampaignId) return;

        const success = await deleteCampaign(selectedCampaignId);

        if (success) {
            showToast('Campaign deleted successfully', 'success');
            setDeleteModalOpen(false);
            setSelectedCampaignId(null);
        } else {
            showToast('Failed to delete campaign', 'error');
        }
    };

    const handleStopConfirm = async () => {
        if (!selectedStopCampaignId) return;

        setStopLoadingId(selectedStopCampaignId);
        const success = await stopCampaign(selectedStopCampaignId);
        setStopLoadingId(null);

        if (success) {
            showToast('Campaign stopped successfully', 'success');
            setStopModalOpen(false);
            setSelectedStopCampaignId(null);
        } else {
            showToast('Failed to stop campaign', 'error');
        }
    };

    const handleExecuteCampaign = async (id: number) => {
        try {
            setExecuteLoadingId(id);
            const result = await campaignApi.execute(id);
            showToast(result.message, 'success');
            await fetchWithFilters();
        } catch {
            showToast('Failed to execute campaign', 'error');
        } finally {
            setExecuteLoadingId(null);
        }
    };

    const handlePageChange = (newPage: number) => {
        fetchWithFilters({ page: newPage });
    };

    const hasFilters = Boolean(searchTerm.trim() || statusFilter);

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 px-4 py-10">
                <div className="mx-auto max-w-xl rounded-2xl border border-red-100 bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-xl font-bold text-red-600">
                        !
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">Unable to load campaigns</h2>
                    <p className="mt-2 text-sm text-slate-600">{error}</p>
                    <Button onClick={() => fetchCampaigns()} className="mt-6">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(219,234,254,0.9),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(240,171,252,0.38),_transparent_30%),linear-gradient(180deg,_#f8fafc_0%,_#eef4ff_100%)]">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                <div className="mb-6 overflow-hidden rounded-3xl border border-white/70 bg-white/65 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
                    <div className="px-6 py-7 text-slate-950 sm:px-8">
                        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">SMS Operations</p>
                                <h1 className="mt-2 text-3xl font-bold tracking-tight">Campaigns</h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                                    Search, execute, and monitor campaigns from a compact operational list.
                                </p>
                            </div>

                            <Button
                                variant="primary"
                                onClick={() => navigate('/campaigns/create')}
                                className="w-full sm:w-auto"
                            >
                                + New Campaign
                            </Button>
                        </div>
                    </div>

                    <div className="grid gap-3 border-y border-white/70 bg-white/45 p-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-2xl border border-white/70 bg-white/70 p-4 shadow-sm">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Showing</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{summary.total}</p>
                        </div>
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4 shadow-sm">
                            <p className="text-xs font-medium uppercase tracking-wide text-blue-600">Active</p>
                            <p className="mt-1 text-2xl font-bold text-blue-900">{summary.active}</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/80 p-4 shadow-sm">
                            <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">Completed</p>
                            <p className="mt-1 text-2xl font-bold text-emerald-900">{summary.completed}</p>
                        </div>
                        <div className="rounded-2xl border border-red-100 bg-red-50/80 p-4 shadow-sm">
                            <p className="text-xs font-medium uppercase tracking-wide text-red-600">Failed</p>
                            <p className="mt-1 text-2xl font-bold text-red-900">{summary.failed}</p>
                        </div>
                    </div>

                    <div className="p-4 sm:p-5">
                        <form onSubmit={handleSearch} className="space-y-4">
                            <div className="flex flex-col gap-3 lg:flex-row">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search by campaign name, sender, or keyword..."
                                        className="w-full rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                    />
                                </div>

                                <div className="flex gap-2">
                                    <Button type="submit" variant="primary" disabled={loading}>
                                        Search
                                    </Button>

                                    {hasFilters && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleClearFilters}
                                            disabled={loading}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {statusOptions.map((option) => {
                                    const isActive = statusFilter === option.value;

                                    return (
                                        <button
                                            key={option.label}
                                            type="button"
                                            onClick={() => handleStatusFilter(option.value as CampaignStatus | '')}
                                            className={[
                                                'whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition',
                                                isActive
                                                    ? 'border-slate-900 bg-slate-900 text-white'
                                                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                                            ].join(' ')}
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </form>
                    </div>
                </div>

                {loading && campaigns.length === 0 ? (
                    <div className="rounded-3xl border border-slate-200 bg-white py-16 shadow-sm">
                        <LoadingSpinner text="Loading campaigns..." />
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl">
                            📣
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900">
                            {hasFilters ? 'No matching campaigns found' : 'No campaigns yet'}
                        </h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
                            {hasFilters
                                ? 'Try changing your search term or status filter.'
                                : 'Create your first campaign to start sending and monitoring SMS activity.'}
                        </p>

                        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                            {hasFilters && (
                                <Button variant="outline" onClick={handleClearFilters}>
                                    Clear Filters
                                </Button>
                            )}
                            <Button onClick={() => navigate('/campaigns/create')}>
                                Create Campaign
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/70 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
                            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                                <div>
                                    <h2 className="text-sm font-semibold text-slate-900">Campaign list</h2>
                                    <p className="mt-1 text-xs text-slate-500">
                                        Page {pagination.page} of {pagination.totalPages}
                                    </p>
                                </div>

                                {loading && (
                                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                                        Refreshing...
                                    </span>
                                )}
                            </div>

                            <div className="hidden grid-cols-[minmax(220px,1.8fr)_160px_130px_130px_140px] gap-4 border-b border-slate-200/70 bg-white/50 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
                                <div>Campaign</div>
                                <div>Progress</div>
                                <div>Success</div>
                                <div>Revenue</div>
                                <div className="text-right">Actions</div>
                            </div>

                            <div className={executeLoadingId ? 'pointer-events-none divide-y divide-slate-100 opacity-75' : 'divide-y divide-slate-100'}>
                                {campaigns.map((campaign) => (
                                    <CampaignCard
                                        key={campaign.id}
                                        campaign={campaign}
                                        onView={(id) => navigate(`/campaigns/${id}`)}
                                        onExecute={handleExecuteCampaign}
                                        onStop={handleStopClick}
                                        onDelete={handleDeleteClick}
                                    />
                                ))}
                            </div>
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm text-slate-600">
                                    Page <span className="font-semibold text-slate-900">{pagination.page}</span> of{' '}
                                    <span className="font-semibold text-slate-900">{pagination.totalPages}</span>
                                </p>

                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={!pagination.hasPrev || loading}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={!pagination.hasNext || loading}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Delete Campaign"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm}>
                            Delete Campaign
                        </Button>
                    </div>
                }
            >
                <div className="space-y-3">
                    <p className="text-sm leading-6 text-slate-600">
                        This will permanently delete the campaign, including its associated contacts and messages.
                    </p>
                    <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                        This action cannot be undone.
                    </p>
                </div>
            </Modal>

            <Modal
                isOpen={stopModalOpen}
                onClose={() => setStopModalOpen(false)}
                title="Stop Campaign"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setStopModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleStopConfirm} loading={stopLoadingId !== null}>
                            Stop Campaign
                        </Button>
                    </div>
                }
            >
                <div className="space-y-3">
                    <p className="text-sm leading-6 text-slate-600">
                        Stopping a campaign will halt further deliveries and mark it as cancelled.
                    </p>
                    <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
                        Messages already sent will remain in the system, but no new sends will be queued.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default CampaignsPage;
